import { get } from 'svelte/store';
import { profile_data } from 'src/stores/account/user';
import { save_profile } from 'src/helpers/profile_helpers';
import type { EnergyLocationData } from '../api/verivoxApi';
import {
	VERIVOX_OFFERS_BANNER_ID,
	VERIVOX_SETUP_BANNER_ID,
	VERIVOX_SNOOZE_MS,
	type VerivoxBannerSurface,
} from 'src/helpers/banner_views/banner-ids';
import {
	clearBanner,
	clearBannerSurface,
	dismissBanner,
	getBanner,
	isBannerVisible,
} from 'src/helpers/banner_views/banner-views-helpers';
import { ensureBannerViewsMigrations } from 'src/helpers/banner_views/banner-views-migrate';

const DE_USERS_KEY = 'de_users';
const VERIVOX_INTEGRATION_KEY = 'verivox_integration';

/** @deprecated Prefer VerivoxBannerSurface from banner-ids — kept for call-site stability. */
export type VerivoxDismissSurface = VerivoxBannerSurface;

export type VerivoxDismissalEntry = {
	dismissed: boolean;
	dismissalDate: number;
};

export type VerivoxDismissals = Partial<Record<VerivoxDismissSurface, VerivoxDismissalEntry>>;

export type VerivoxBannerKind = 'setup' | 'offers';

const bannerIdForKind = (kind: VerivoxBannerKind = 'setup') =>
	kind === 'offers' ? VERIVOX_OFFERS_BANNER_ID : VERIVOX_SETUP_BANNER_ID;

const getIntegrationData = (): EnergyLocationData | Record<string, unknown> | null => {
	const profileData = get(profile_data);
	return profileData?.[DE_USERS_KEY]?.[VERIVOX_INTEGRATION_KEY] ?? null;
};

/**
 * Read legacy dismissals map from de_users (migration / fallback only).
 */
export const getVerivoxDismissals = (
	data?: EnergyLocationData | Record<string, unknown> | null,
): VerivoxDismissals => {
	const source = data ?? getIntegrationData();
	if (!source || typeof source !== 'object') {
		return {};
	}

	const dismissals: VerivoxDismissals = {
		...((source as EnergyLocationData).dismissals || {}),
	};

	const legacy = source as EnergyLocationData;
	if (legacy.dismissed && !dismissals.consumption) {
		dismissals.consumption = {
			dismissed: true,
			dismissalDate: legacy.dismissalDate ?? Date.now(),
		};
	}

	return dismissals;
};

export const getVerivoxDismissalEntry = (
	surface: VerivoxDismissSurface,
): VerivoxDismissalEntry | null => {
	const entry = getVerivoxDismissals()[surface];
	return entry?.dismissed ? entry : null;
};

const isLegacySurfaceDismissed = (surface: VerivoxDismissSurface, now: number): boolean => {
	const entry = getVerivoxDismissals()[surface];
	if (!entry?.dismissed) return false;
	if (entry.dismissalDate) {
		return now - entry.dismissalDate < VERIVOX_SNOOZE_MS;
	}
	return true;
};

/**
 * True when the Verivox banner should stay hidden on this surface.
 * Prefer banner_views; fall back to de_users.dismissals until migrated.
 */
export const isVerivoxSurfaceDismissed = (
	surface: VerivoxDismissSurface,
	kind: VerivoxBannerKind = 'setup',
	now: number = Date.now(),
): boolean => {
	try {
		const bannerId = bannerIdForKind(kind);
		const record = getBanner(bannerId);
		const hasSurfaceState = Boolean(record?.surfaces?.[surface]);

		if (hasSurfaceState || record?.convertedAt !== undefined) {
			return !isBannerVisible(bannerId, surface, now);
		}

		return isLegacySurfaceDismissed(surface, now);
	} catch (error) {
		console.error('Error checking Verivox dismissal state:', error);
		return false;
	}
};

/** Strip a surface (or all) from legacy de_users.dismissals after banner_views write. */
const stripLegacyDismissals = async (surface?: VerivoxDismissSurface): Promise<void> => {
	try {
		const profileData = get(profile_data);
		const existingDeUsers = profileData?.[DE_USERS_KEY];
		const existingIntegration = existingDeUsers?.[VERIVOX_INTEGRATION_KEY] as
			| EnergyLocationData
			| undefined;
		if (!existingDeUsers || !existingIntegration) return;

		const updatedIntegration: EnergyLocationData = { ...existingIntegration };
		delete updatedIntegration.dismissed;
		delete updatedIntegration.dismissalDate;

		if (!surface) {
			delete updatedIntegration.dismissals;
		} else {
			const dismissals = { ...getVerivoxDismissals(existingIntegration) };
			delete dismissals[surface];
			if (Object.keys(dismissals).length === 0) {
				delete updatedIntegration.dismissals;
			} else {
				updatedIntegration.dismissals = dismissals;
			}
		}

		const updatedDeUsers = {
			...existingDeUsers,
			[VERIVOX_INTEGRATION_KEY]: updatedIntegration,
		};

		profile_data.update((current) => ({
			...current,
			[DE_USERS_KEY]: updatedDeUsers,
		}));

		await save_profile(DE_USERS_KEY, updatedDeUsers);
	} catch (error) {
		console.error('Error stripping legacy Verivox dismissals:', error);
	}
};

export const dismissVerivoxSurface = async (
	surface: VerivoxDismissSurface,
	kind: VerivoxBannerKind = 'setup',
): Promise<void> => {
	try {
		await ensureBannerViewsMigrations();
		const bannerId = bannerIdForKind(kind);
		await dismissBanner(bannerId, {
			scope: 'per-surface',
			surface,
			snoozeMs: VERIVOX_SNOOZE_MS,
			dismissSource: 'user',
		});
		await stripLegacyDismissals(surface);
	} catch (error) {
		console.error('Error saving Verivox dismissal state:', error);
	}
};

export const clearVerivoxSurfaceDismissal = async (
	surface: VerivoxDismissSurface,
): Promise<void> => {
	try {
		await ensureBannerViewsMigrations();
		await clearBannerSurface(VERIVOX_SETUP_BANNER_ID, surface);
		await clearBannerSurface(VERIVOX_OFFERS_BANNER_ID, surface);
		await stripLegacyDismissals(surface);
	} catch (error) {
		console.error('Error clearing Verivox dismissal state:', error);
	}
};

/** Clear all surface dismissals (e.g. Settings "show banners again"). */
export const clearAllVerivoxDismissals = async (): Promise<void> => {
	try {
		await ensureBannerViewsMigrations();
		await clearBanner(VERIVOX_SETUP_BANNER_ID);
		await clearBanner(VERIVOX_OFFERS_BANNER_ID);
		await stripLegacyDismissals();
	} catch (error) {
		console.error('Error clearing all Verivox dismissals:', error);
	}
};
